import { createContext, ReactNode, useContext, useState, useEffect } from 'react'
import { useSessionStorage } from '@/hooks/useSessionStorage'
import {
  delay,
  isFrame,
  redirectToUrl,
  generateShopifyAuthorizeUrl,
  normalizeShopifyDomain,
  getShopifyAdminAppUrl,
} from '@/utilities/misc'
import { upatraAuth } from '@/utilities/apphubApi'
import { ShopInfo } from './types'

type AuthProviderProps = {
  host: string
  shop: string
  children: ReactNode
}

export type AuthContext = {
  host: string
  shopifyDomain: string
  currentShop?: ShopInfo
}

const AuthContext = createContext({} as AuthContext)

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ host, shop, children }: AuthProviderProps) {
  const env = process.env.NODE_ENV
  const shopifyDomain = normalizeShopifyDomain(shop)
  const [shopInfos, setShopInfos] = useSessionStorage<ShopInfo[]>('shop-infos', [])
  const [currentShop, setCurrentShop] = useState<ShopInfo>()

  useEffect(() => {
    ;(async () => {
      const shop = shopInfos.find((i) => i.shopifyDomain === shopifyDomain)

      if (shop) {
        setCurrentShop(shop)
        const redirectUrl =
          env !== 'development'
            ? getShopifyAdminAppUrl(shopifyDomain)
            : `${window.location.origin}?host=${host}&shop=${shopifyDomain}`

        !isFrame && env !== 'development' && redirectToUrl(redirectUrl)
        return
      }

      const result = await upatraAuth(window.location.search)
      await delay(300)
      if (!result || !result.shopifyDomain) {
        const authorizeUrl = generateShopifyAuthorizeUrl(shopifyDomain)
        redirectToUrl(authorizeUrl)
      } else {
        setShopInfo({
          shopifyDomain: result.shopifyDomain,
          upatraToken: result.upatraToken,
        })
      }
    })()
  }, [shopifyDomain, shopInfos])

  const setShopInfo = (shopInfo: ShopInfo) => {
    const shop = shopInfos.find((i) => i.shopifyDomain === shopInfo.shopifyDomain)

    setShopInfos((currShops) => {
      if (!shop) {
        return [
          ...currShops,
          {
            shopifyDomain: shopInfo.shopifyDomain,
            upatraToken: shopInfo.upatraToken,
          },
        ]
      } else {
        return currShops.map((s) => {
          if (s.shopifyDomain === shopInfo.shopifyDomain)
            return {
              ...s,
              upatraToken: shopInfo.upatraToken,
            }
          else return s
        })
      }
    })
  }

  return (
    <AuthContext.Provider
      value={{
        host,
        shopifyDomain,
        currentShop,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
